/* eslint-disable import/no-anonymous-default-export */
import React, { useState, useEffect } from 'react';
import { Icon, Button, IconButton, Select, MenuItem, CircularProgress, Switch, FormGroup, FormControlLabel } from '@mui/material';
import { filterStyles } from './styles';
import { get, size } from 'global/lib/lodash';
import sendData from 'redux/actions/sendData';
import ScorecardToggle from './ScorecardToggle';
import SearchBar from 'global/components/SearchBar';
import { removeOverflowFromBody } from 'global/utils';
import useDebounce from 'global/hooks/useDebounce';
import clsx from 'clsx';
import { ButtonDrop } from '../../../global/components/Button';
import TaskBanner from './TaskBanner';
import { useUser } from '../../../global/hooks/useUser';
import { lowerCaseAll, stopProp } from '../../../global/utils';
import { ScoreCopyModal } from './ScoreCopyModal';
import { Row } from '../../../global/components/row';
import Confirm from '../../../global/components/confirm';

const specialchars = ['_', '*', '/', `\\`, '[', `]`, `{`, `}`, `~`, '=', '+'];

export const TARGETOPTIONS = [
  { title: 'Active', value: 'active' },
  { 
    title: 'Review Scores', 
    value: 'review', 
    show: p => get(p, 'assessment.status') != 'draft' && !![...get(p, 'assessment.editors', []), ...get(p, 'assessment.authors', []), ...get(p, 'assessment.viewers', []), ...get(p, 'assessment.assignees', [])].find(a => a.id == get(p, 'user.me.id')) && get(p, 'assessment.availableStatus', []).some(f => 'review' === f)
  },
  // { 
  //   title: 'Draft Scores', 
  //   value: 'draft', 
  //   show: p => !![...get(p, 'assessment.editors', []), ...get(p, 'assessment.authors', [])].find(a => a.id == get(p, 'user.me.id')) && get(p, 'assessment.availableStatus', []).some(f => 'draft' === f)
  // },
  { 
    title: p => {
      const str = get(p, 'assessment.availableStatus', []).includes('target') ? 'Target Scores' : 'Assign Previous Targets';
      // only editors can delete target scores
      if (!str.toLowerCase().includes('assign') && !![...get(p, 'assessment.editors', []), ...get(p, 'assessment.authors', [])].find(a => a.id == get(p, 'user.me.id'))) {
        return <Row>{str}<IconButton style={{ marginLeft: 5 }} onClick={p.deleteScores} size="small" title="Delete Assigned Target Scores"><Icon>delete_forever</Icon></IconButton></Row>
      }
      return str;
    },
    value: 'target',
    // only show "Assign" button to edits/authors, but show already assigned Target values (if available) to anyone
    show: p => !p.isTemplate && (get(p, 'assessment.availableStatus', []).includes('target') || !![...get(p, 'assessment.editors', []), ...get(p, 'assessment.authors', [])].find(a => a.id == get(p, 'user.me.id'))),
    onSelect: (p, opts = {})  => {
      if (p.title && !p.title.props && p.title.toLowerCase().includes('assign')) {
        const sc = get(opts.scorecards, opts.scorecardIndex || 0);

        if (sc) {
          opts && opts.setScoreCopyModal(s => ({ 
            ...s, 
            open: true,
            title: p.title,
            assessment: get(opts, 'assessment'),
            scorecardId: sc.id,
          }))
        }
      }
    },
  },
  {
    title: p => {
      const str = get(p, 'assessment.availableStatus', []).includes('goal') ? 'New Target Scores' : 'Add New Target Scores';
      // only editors can delete target scores
      if (!str.toLowerCase().includes('add new') && !![...get(p, 'assessment.editors', []), ...get(p, 'assessment.authors', [])].find(a => a.id == get(p, 'user.me.id'))) {
        return <Row>{str}<IconButton style={{ marginLeft: 5 }} onClick={e => p.deleteScores(e, { dest: 'new-target-scores' })} size="small" title="Delete Target Scores"><Icon>delete_forever</Icon></IconButton></Row>
      }
      return str;
    },
    // title: p => get(p, 'assessment.availableStatus', []).includes('goal') ? 'New Target Scores' : 'Add New Target Scores',
    value: 'goal',
    label: 'new target', 
    selText: p => p.useDiff ? <>Showing the difference between your scores ( <strong style={{fontStyle: "normal"}}>|</strong> ) and <strong>new target</strong> scores (<strong>+/-</strong>)</> : <>Showing <strong>new target</strong> scores </>,
    onSelect: async (p, opts = {})  => {
      if (p.title && !p.title.props && p.title.toLowerCase().includes('add new')) {
        const aid = get(opts, 'assessment.data.id');
        await sendData({
            url: `/assessments/${aid}/new-target-scores`,
            method: 'PATCH',
            data: {
              data: {
                type: "assessments",
                id: aid
              }
            }
        })

        get(opts, 'scores.update', () => 0)();
      }
    },
    show: p => { 
      const isLocation = get(p, 'selected.listGroup') == 'location';
      const config = get(p,`scorecards.${p?.scorecardIndex}.config`)
      // Don't show if scoring isn't enabled for this scorecard or if status is Draft
      return !isLocation && !(config?.hasSubScores && !config?.editScoreInfo) && !(get(p, 'assessment.status', '') === 'draft');
    } 
  },   
  {
    title: 'Previous to New Target Scores',
    value: 'targetGoal', selText: p => p.useDiff ? <>Showing the difference between <strong>previous target</strong> scores ( <strong style={{fontStyle: "normal"}}>|</strong> ) and <strong>new target</strong> scores (<strong>+/-</strong>)</> : <>Showing <strong>new target</strong> scores </>,
    show: p => size(get(p, 'assessment.availableStatus', []).filter(f => ['goal', 'target'].includes(f))) == 2 },
]

export const getTargetOptions = (opts = {}) => {
  opts = {
    assessment: {},
    user: {},
    ...opts,
  }

  return TARGETOPTIONS.filter(f => get(f, 'show', () => true)(opts)).map(o => ({ ...o, title: typeof o.title == 'function' ? o.title(opts) : o.title }));
}

export default (props) => {
  const { selected, setFilter, buttons, changeSearch, searchString, loading, defaultFilters } = props;
  const [searchStringLocal, setSearchStringLocal] = useState('');
  const [scoreCopyModal, setScoreCopyModal] = useState({
    ...props,
    open: false,
    isShowingNone: (v) => {
      setScoreCopyModal(s => ({ ...s, showedNone: v }))
    },
    onClose: () => {
      setScoreCopyModal(s => {
        if (s.showedNone) {
          setFilter(f => ({ ...f, assessment_status: 'active' }))
        }
        return { ...s, open: !s.open }
      });
    }
  })
  const [confirm, setConfirm] = useState({})
  const user = useUser();

  const _searchStringDebounced = useDebounce(searchStringLocal, 700);
  const searchStringDebounced = size(_searchStringDebounced) >= 3 || specialchars.find((f) => _searchStringDebounced.indexOf(f) > -1) ? _searchStringDebounced : null;

  useEffect(() => {
    if (searchString !== searchStringLocal) {
      if (!searchString) {
        setSearchStringLocal('');
      } else {
        setSearchStringLocal(searchString);
      }
    }
  }, [searchString]);

  useEffect(() => {
    if (searchString !== searchStringLocal) {
      (async () => {
        try {
          // Promise.race between handleAskWallter and a 1s timeout
          await Promise.race([
            handleAskWallter(),
            new Promise((_, reject) =>
              setTimeout(() => reject(new Error("Timeout")), 1000)
            ),
          ]);
        } catch (e) {
          if (e.message === "Timeout") {
            console.warn("handleAskWallter took too long, moving on");
          } else {
            console.error(e);
          }
        } finally {
          changeSearch(searchStringDebounced);
        }
      })();
    }
  }, [searchStringDebounced]);

  const handleAskWallter = async () => {
    const scorecardIndex = get(props,'scorecardIndex');
    const scorecardId = get(props,`scorecards.${scorecardIndex}.id`);
    if (!searchStringDebounced || !scorecardId) return;
    const wallterResponse = await sendData({
      url: `/wallter?scorecard_id=${scorecardId}&query=${searchStringDebounced}`,
    });
    const responseData = wallterResponse.data || {};

    // filter out the request id key
    const items = Object.values(responseData).filter((item) => typeof item === 'object' && item.checkpoint_id);
    const checkpointIds = items.map((item) => item.checkpoint_id || item.checkpointId).filter(Boolean);

    setFilter((f) => ({...f, suggestedCpIds: checkpointIds}));
  };

  const classes = filterStyles();
  const selVal = get(selected, 'value');
  const loadingIcon = loading || (searchString && searchString !== searchStringLocal);
  const isDefault = !searchString && Object.keys(defaultFilters).reduce((agg, k) => {
    if (get(selected, k) && defaultFilters[k] !== get(selected, k)) { return false }
    return agg;
  }, true);

  const updateScoresAfterCopy = () => {
    get(props, 'scores.update', () => 0)();
  }

  const deleteScores = (e, opts = {}) => {
    stopProp(e);

    opts = {
      dest: 'target-scores',
      ...opts,
    }

    const aid = get(props, 'assessment.data.id');
    setConfirm({
      title: 'Delete Target Scores',
      content: 'Are you sure you want to delete the Target Scores from this Assessment?',
      onOk: async () => {
        try {
          await sendData({
              url: `/assessments/${aid}/${opts.dest}`,
              method: 'DELETE',
          })
  
          setConfirm({});
          updateScoresAfterCopy();
        } catch (err) {
          alert(err.toString())
        }
      },
      onCancel: () => {
        setConfirm({});
      }
    })
  }

  const renderButtonDrop = () => {
    const options = getTargetOptions({ ...props, user, assessment: props.assessment.data, deleteScores });

    const sel = options.find(o => o.value == selected.assessment_status) || {};

    const toggleDiff = () => {
      setFilter(f => ({ ...f, useDiff: !f.useDiff }));
    }

    return size(options) > 1 ? { 
      sel, 
      text: (
        <div className={classes.droptext}>
          {sel.value == 'active' && `Showing your scores`}
          {sel.value != 'active' && (selected.useDiff ? 
            <span>{sel.selText ? sel.selText({ ...selected }) : (<>Showing the difference between <strong>{lowerCaseAll(sel?.label ?? sel.value)}</strong> scores ( <strong style={{fontStyle: "normal"}}>|</strong> ) and your scores (<strong>+/-</strong>)</>)}<span className='difflink' onClick={toggleDiff}>{`[show score]`}</span></span>
            : <span>{sel.selText ? sel.selText({ ...selected }) : <>Showing <strong>{lowerCaseAll(sel?.label ?? sel.value)}</strong> scores </>}<span className='difflink' onClick={toggleDiff}>{`[show diff]`}</span></span>)}
        </div>
      ),
      comp: (
        <div className={classes.buttonDropContainer}>
          <ButtonDrop 
            className={classes.buttonDrop}
            onSelect={o => {
              o.onSelect && o.onSelect(o, { ...props, setScoreCopyModal });
              setFilter(f => ({ ...f, assessment_status: o.value, useDiff: ['goal', 'target'].includes(o.value) ? true : f.useDiff }));
            }} 
            variant="default" 
            icon="list_alt" 
            options={options.map(o => ({ ...o, selected: o.value == selected.assessment_status }))} 
          >
            <Icon className='innericon'>arrow_drop_down</Icon>
          </ButtonDrop>
        </div>
      )
    } : {comp: undefined, text: undefined}
  }

  const bdrop = renderButtonDrop();

  return (
    <>
      <div className={classes.container}>
        <ScorecardToggle {...props} />
        <div className={classes.wrapperWithBorder}>
          <TaskBanner {...props.taskBanner} />
          {!get(props, 'taskBanner.showSelectBoxes') && <div className={classes.wrapper}>
            {bdrop.comp}
            <SearchBar
              onClear={() => {
                setSearchStringLocal('');
              }}
              SearchIcon={loadingIcon ? <CircularProgress size={24} style={{ width: 24, height: 24, marginLeft: 12 }} />
                : isDefault ? <Icon style={{ color: 'grey', width: 48, height: 48, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>search</Icon>
                  : <IconButton
                onClick={() => { setFilter(defaultFilters); changeSearch('') }}
                size="large"><Icon>replay</Icon></IconButton>}
              changeSearch={setSearchStringLocal}
              searchString={searchStringLocal}
              classes={classes.searchContainer}
            />
            {buttons.map((button, i) => {
              const show = get(button, 'show', () => true)(props);
              if (!show) { 
                return null;
              }
              const type = get(button, 'type', 'default');
              if (type === 'select') {
                const selectSelected = button.options.find((o) => o.val === get(selected, button.val));
                const active = get(selectSelected, 'val') !== get(defaultFilters, get(button, 'val'));
                return (
                  <Select
                    key={button.val}
                    value={get(selectSelected, 'val')}
                    variant="filled"
                    defaultValue="N/A"
                    className={clsx(classes.select, active ? classes.selectSelected : undefined)}
                    disableUnderline
                    onChange={(e) => {
                      setFilter((f) => ({ ...f, [button.val]: e.target.value }))
                      button.onChange && button.onChange(e.target.value);
                    }}
                    onClose={() => removeOverflowFromBody()}
                  >
                    {button.options.map((opt, i) => {
                      return (
                        <MenuItem key={i} value={get(opt, 'val')}>
                          {opt.text}
                        </MenuItem>
                      );
                    })}
                  </Select>
                );
              } else if (type === 'switch') {
                const on = !!get(props, button.val);
                return (
                  <div key={button.val} className={classes.switchContainer}>
                    <FormGroup row className={classes.center}>
                      <FormControlLabel
                        classes={{ root: classes.switchText }}
                        control={
                          <Switch
                            checked={on}
                            onChange={(e) => button.onChange(e.target.checked)}
                            inputProps={{ 'aria-label': 'secondary checkbox' }}
                            disabled={button.disabled ? button.disabled(props) : false}
                          />
                        }
                        label={get(button, ['text', on ? 'on' : 'off'], get(button, 'text'))}
                      />
                    </FormGroup>
                  </div>
                );
              }

              const isSelected = selVal === get(button, 'val');
              return (
                <Button
                  key={i}
                  className={classes.buttonMargin}
                  size={'small'}
                  onClick={() => setFilter((f) => ({ ...f, value: button.val }))}
                  variant="contained"
                  color={isSelected ? 'primary' : undefined}
                  disableElevation
                >
                  {button.text}
                </Button>
              );
            })}
          </div>}
        </div>
        {bdrop.text}
        <ScoreCopyModal {...scoreCopyModal} updateScoresAfterCopy={updateScoresAfterCopy} />
        <Confirm {...confirm} />
      </div>
    </>
  );
};
